<div class="space-y-6">
  <app-page-header
    [backTo]="['/rentals']"
    backLabel="Retour aux locations"
  >
    @if (rental(); as r) {
      <div slot="actions" class="flex flex-wrap items-center gap-2">
        @if (invoiceId() != null) {
          <a
            [routerLink]="['/invoices', invoiceId()]"
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Voir la facture
          </a>
        } @else if (!invoiceLoading() && invoiceFetched()) {
          <span class="text-xs text-gray-400">Pas de facture</span>
        }
        <button
          type="button"
          (click)="openDelete()"
          class="inline-flex items-center justify-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10"
        >
          Supprimer
        </button>
      </div>
    }
  </app-page-header>

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      loadingMessage="Chargement de la location…"
      (retry)="fetch()"
    />
  } @else if (rental(); as r) {
    <!-- Header card : vehicle + status + KPIs -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
          @if (r.vehicle?.imgCover) {
            <img [src]="r.vehicle?.imgCover" [alt]="vehicleName()" class="h-24 w-36 rounded-xl object-cover ring-1 ring-gray-200 dark:ring-gray-800" />
          } @else {
            <span class="flex h-24 w-36 items-center justify-center rounded-xl bg-gray-100 text-gray-400 dark:bg-gray-800">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M6.5 5.75A1.25 1.25 0 0 0 5.3 6.62l-1.05 3.13H3.75a.75.75 0 0 0-.75.75V14a.75.75 0 0 0 .75.75H4.5v1.5a1.25 1.25 0 1 0 2.5 0v-1.5h10v1.5a1.25 1.25 0 1 0 2.5 0v-1.5h.75A.75.75 0 0 0 21 14v-3.5a.75.75 0 0 0-.75-.75h-.5L18.7 6.62A1.25 1.25 0 0 0 17.5 5.75h-11Z"/></svg>
            </span>
          }
          <div class="space-y-2">
            <div class="flex flex-wrap items-center gap-3">
              <h1 class="text-xl font-semibold text-gray-800 dark:text-white/90">Location #{{ r.id }}</h1>
              <span
                class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                [class.bg-brand-50]="statusMeta[r.rentalStatusEnum].palette === 'brand'"
                [class.text-brand-700]="statusMeta[r.rentalStatusEnum].palette === 'brand'"
                [class.dark:bg-brand-500/15]="statusMeta[r.rentalStatusEnum].palette === 'brand'"
                [class.dark:text-brand-400]="statusMeta[r.rentalStatusEnum].palette === 'brand'"
                [class.bg-blue-light-50]="statusMeta[r.rentalStatusEnum].palette === 'blue-light'"
                [class.text-blue-light-700]="statusMeta[r.rentalStatusEnum].palette === 'blue-light'"
                [class.dark:bg-blue-light-500/15]="statusMeta[r.rentalStatusEnum].palette === 'blue-light'"
                [class.dark:text-blue-light-400]="statusMeta[r.rentalStatusEnum].palette === 'blue-light'"
                [class.bg-warning-50]="statusMeta[r.rentalStatusEnum].palette === 'warning'"
                [class.text-warning-700]="statusMeta[r.rentalStatusEnum].palette === 'warning'"
                [class.dark:bg-warning-500/15]="statusMeta[r.rentalStatusEnum].palette === 'warning'"
                [class.dark:text-warning-400]="statusMeta[r.rentalStatusEnum].palette === 'warning'"
                [class.bg-gray-100]="statusMeta[r.rentalStatusEnum].palette === 'gray'"
                [class.text-gray-700]="statusMeta[r.rentalStatusEnum].palette === 'gray'"
                [class.dark:bg-gray-800]="statusMeta[r.rentalStatusEnum].palette === 'gray'"
                [class.dark:text-gray-300]="statusMeta[r.rentalStatusEnum].palette === 'gray'"
                [class.bg-error-50]="statusMeta[r.rentalStatusEnum].palette === 'error'"
                [class.text-error-700]="statusMeta[r.rentalStatusEnum].palette === 'error'"
                [class.dark:bg-error-500/15]="statusMeta[r.rentalStatusEnum].palette === 'error'"
                [class.dark:text-error-400]="statusMeta[r.rentalStatusEnum].palette === 'error'"
              >
                {{ statusMeta[r.rentalStatusEnum].label }}
              </span>
              @if (r.averageRating != null) {
                <span class="inline-flex items-center gap-1 text-sm text-warning-600 dark:text-warning-400">
                  ★ {{ r.averageRating | number:'1.1-1' }}
                </span>
              }
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              @if (r.vehicle; as v) {
                <a [routerLink]="['/vehicles', v.id]" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">
                  {{ vehicleName() }}
                </a>
                <span class="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
                <span class="font-mono">{{ v.licencePlate || '—' }}</span>
              } @else {
                <span class="text-gray-400">Véhicule non renseigné</span>
              }
            </p>
            @if (r.vehicle?.vehicleOwner; as o) {
              <div class="mt-2 inline-flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5 dark:border-gray-700">
                <span class="text-[10px] uppercase tracking-wide text-gray-400">Propriétaire</span>
                <app-user-cell [user]="o.user" [fallbackId]="o.userId" size="xs" />
              </div>
            }
          </div>
        </div>
      </div>

      <!-- KPI grid -->
      <div class="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Montant total</p>
          <p class="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">
            {{ r.totalCost | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">XOF</span>
          </p>
        </div>
        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Durée</p>
          <p class="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">
            @if (durationDays(); as d) { {{ d }} jour(s) } @else { — }
          </p>
          @if (r.nbHours) {
            <p class="text-xs text-gray-400">{{ r.nbHours }}h facturées</p>
          }
        </div>
        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Frais de retard</p>
          <p class="mt-1 text-lg font-semibold"
             [class.text-error-700]="(r.lateFee ?? 0) > 0"
             [class.dark:text-error-400]="(r.lateFee ?? 0) > 0"
             [class.text-gray-800]="(r.lateFee ?? 0) === 0"
             [class.dark:text-white/90]="(r.lateFee ?? 0) === 0">
            {{ (r.lateFee ?? 0) | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">XOF</span>
          </p>
        </div>
        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Contrat signé</p>
          <p class="mt-1 text-sm font-medium"
             [class.text-brand-700]="r.docusignSubmissionStatus === 'completed'"
             [class.dark:text-brand-400]="r.docusignSubmissionStatus === 'completed'"
             [class.text-warning-700]="r.docusignSubmissionStatus === 'awaiting'"
             [class.dark:text-warning-400]="r.docusignSubmissionStatus === 'awaiting'"
             [class.text-error-700]="r.docusignSubmissionStatus === 'declined'"
             [class.dark:text-error-400]="r.docusignSubmissionStatus === 'declined'"
             [class.text-gray-400]="!r.docusignSubmissionStatus">
            @switch (r.docusignSubmissionStatus) {
              @case ('completed') { Signé }
              @case ('awaiting') { En attente }
              @case ('declined') { Refusé }
              @default { — }
            }
          </p>
        </div>
      </div>
    </section>

    <div class="grid gap-6 lg:grid-cols-2">
      <!-- Période & lieux -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Période & lieux</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Début</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ r.startDate | ldate:'dd/MM/yyyy HH:mm' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Fin prévue</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ r.endDate | ldate:'dd/MM/yyyy HH:mm' }}</dd>
          </div>
          @if (r.actualReturnDate) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">Retour réel</dt>
              <dd class="text-right" [class.text-error-700]="returnedLate()" [class.dark:text-error-400]="returnedLate()" [class.text-gray-800]="!returnedLate()" [class.dark:text-white/90]="!returnedLate()">
                {{ r.actualReturnDate | ldate:'dd/MM/yyyy HH:mm' }}
                @if (returnedLate()) { <span class="ml-1 text-xs">(en retard)</span> }
              </dd>
            </div>
          }
          <div class="flex justify-between gap-4 border-t border-gray-100 pt-3 dark:border-gray-800">
            <dt class="text-gray-500 dark:text-gray-400">Lieu de prise</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ r.pickupLocation || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Lieu de retour</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ r.returnLocation || '—' }}</dd>
          </div>
        </dl>
      </section>

      <!-- Client -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Client</h2>
        @if (r.customer; as c) {
          <div class="mt-4">
            <app-user-cell [user]="c.user" [fallbackId]="c.userId" size="md" />
            <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">Client #{{ c.id }}</p>
          </div>
          <dl class="mt-4 grid gap-3 text-sm">
            @if (c.licenseNumber) {
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Permis</dt>
                <dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ c.licenseNumber }}</dd>
              </div>
            }
            @if (c.licenseExpiryDate) {
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Expiration permis</dt>
                <dd class="text-right text-gray-800 dark:text-white/90">{{ c.licenseExpiryDate | ldate:'dd/MM/yyyy' }}</dd>
              </div>
            }
            @if (c.verificationStatus) {
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Vérification</dt>
                <dd class="text-right">
                  <span
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    [class.bg-brand-50]="c.verificationStatus === 'VERIFIED'"
                    [class.text-brand-700]="c.verificationStatus === 'VERIFIED'"
                    [class.bg-warning-50]="c.verificationStatus === 'PENDING'"
                    [class.text-warning-700]="c.verificationStatus === 'PENDING'"
                    [class.bg-error-50]="c.verificationStatus === 'REJECTED'"
                    [class.text-error-700]="c.verificationStatus === 'REJECTED'"
                  >
                    @switch (c.verificationStatus) {
                      @case ('VERIFIED') { Vérifié }
                      @case ('PENDING') { En attente }
                      @case ('REJECTED') { Rejeté }
                    }
                  </span>
                </dd>
              </div>
            }
            @if (c.city || c.country) {
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Localisation</dt>
                <dd class="text-right text-gray-800 dark:text-white/90">
                  {{ c.city || '' }}{{ c.city && c.country ? ', ' : '' }}{{ c.country || '' }}
                </dd>
              </div>
            }
            @if (c.totalRentals != null) {
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Locations totales</dt>
                <dd class="text-right text-gray-800 dark:text-white/90">{{ c.totalRentals }}</dd>
              </div>
            }
          </dl>
        } @else if (r.userId) {
          <div class="mt-4 space-y-2">
            <p class="text-xs text-gray-500 dark:text-gray-400">Réservation legacy (sans profil client) :</p>
            <app-user-cell [fallbackId]="r.userId" size="md" />
          </div>
        } @else {
          <p class="mt-4 text-sm text-gray-400">Client non renseigné.</p>
        }
      </section>
    </div>

    <!-- Contrat & signature. Pas de ligne « Lien signature » : le lien DocuSign
         est la session de signature DU CLIENT — l'ouvrir depuis l'admin
         permettrait de signer à sa place (#97). Seul le contrat signé
         (rentalContract) est exposé. -->
    @if (r.docusignSubmissionStatus || r.rentalContract || r.qrcode) {
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Contrat & signature</h2>
        @if (!r.rentalContract && r.docusignSubmissionStatus === 'awaiting') {
          <p class="mt-3 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-white/[0.03] dark:text-gray-400">
            Le contrat sera disponible ici une fois signé par le client.
          </p>
        }
        <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          @if (r.docusignSubmissionId) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">ID soumission</dt>
              <dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ r.docusignSubmissionId }}</dd>
            </div>
          }
          @if (r.rentalContract) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">Contrat PDF</dt>
              <dd class="text-right">
                <a [href]="r.rentalContract" target="_blank" rel="noopener" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">
                  Télécharger
                </a>
              </dd>
            </div>
          }
          @if (r.qrcode) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">QR code</dt>
              <dd class="text-right font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{{ r.qrcode }}</dd>
            </div>
          }
        </dl>
      </section>
    }

    <!-- Photos avant / après -->
    @if ((r.photosBefore?.length ?? 0) > 0 || (r.photosAfter?.length ?? 0) > 0) {
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Photos</h2>
        <div class="mt-4 grid gap-6 lg:grid-cols-2">
          <div>
            <p class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Avant ({{ r.photosBefore?.length ?? 0 }})</p>
            @if ((r.photosBefore?.length ?? 0) === 0) {
              <p class="text-xs text-gray-400">Aucune photo.</p>
            } @else {
              <div class="grid grid-cols-3 gap-2 sm:grid-cols-4">
                @for (img of r.photosBefore; track img.id) {
                  <a [href]="img.url" target="_blank" rel="noopener" class="block">
                    <img [src]="img.url" alt="Photo avant" class="aspect-square w-full rounded-md object-cover ring-1 ring-gray-200 dark:ring-gray-800" />
                  </a>
                }
              </div>
            }
          </div>
          <div>
            <p class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Après ({{ r.photosAfter?.length ?? 0 }})</p>
            @if ((r.photosAfter?.length ?? 0) === 0) {
              <p class="text-xs text-gray-400">Aucune photo.</p>
            } @else {
              <div class="grid grid-cols-3 gap-2 sm:grid-cols-4">
                @for (img of r.photosAfter; track img.id) {
                  <a [href]="img.url" target="_blank" rel="noopener" class="block">
                    <img [src]="img.url" alt="Photo après" class="aspect-square w-full rounded-md object-cover ring-1 ring-gray-200 dark:ring-gray-800" />
                  </a>
                }
              </div>
            }
          </div>
        </div>
      </section>
    }

    <!-- Métadonnées -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Métadonnées</h2>
      <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500 dark:text-gray-400">Créée le</dt>
          <dd class="text-right text-gray-800 dark:text-white/90">{{ r.createdAt | ldate:'dd/MM/yyyy HH:mm' }}</dd>
        </div>
        @if (r.updatedAt) {
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Mise à jour</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ r.updatedAt | ldate:'dd/MM/yyyy HH:mm' }}</dd>
          </div>
        }
      </dl>
    </section>
  }
</div>

<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  title="Supprimer la location ?"
  confirmLabel="Supprimer définitivement"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  Cette action est irréversible. La location <strong>#{{ rental()?.id }}</strong> sera marquée comme supprimée.
</app-confirm-modal>
